import {
  array,
  boolean,
  InferInput,
  InferOutput,
  number,
  object,
  optional,
  pipe,
  safeParse,
  string,
  transform,
  union,
  ValiError,
} from 'valibot';

import {
  MusicVideoTrackItemSchema,
  PlaylistTrackItemSchema,
} from '../apiSchema.ts';
import { getOffsetNumber } from '../utils.ts';

export const PlaylistIdTracksRequestArgsSchema = object({
  id: string(),
  limit: optional(number()),
  offset: optional(number()),
  isLibrary: optional(boolean()),
});

export const PlaylistIdTracksResponseSchema = pipe(
  object({
    data: array(union([PlaylistTrackItemSchema, MusicVideoTrackItemSchema])),
    meta: optional(object({
      total: number(),
    })),
    next: optional(string()),
  }),
  transform(({ data, meta, next }) => {
    return {
      tracks: data,
      total: meta?.total,
      ...(next ? { next: getOffsetNumber(next) } : {}),
    };
  }),
);

export const PlaylistIdTracksAPISchema = pipe(
  object({
    data: PlaylistIdTracksResponseSchema,
  }),
);

export type PlaylistIdTracksRequestArgs = InferOutput<
  typeof PlaylistIdTracksRequestArgsSchema
>;

export type PlaylistIdTracksData = InferOutput<
  typeof PlaylistIdTracksResponseSchema
>;

export type PlaylistIdTracksAPIInput = InferInput<
  typeof PlaylistIdTracksAPISchema
>;

export type PlaylistIdTracksAPI = InferOutput<
  typeof PlaylistIdTracksAPISchema
>;

/**
 * Fetches the tracks of a playlist from the MusicKit API.
 * @param {MusicKit.MusicKitInstance} player The MusicKit instance.
 * @param {PlaylistIdTracksRequestArgs} params The parameters for the request.
 * @returns {Promise<PlaylistIdTracksData>} Response data of the playlist.
 */
export const $getPlaylistIdTracks = async (
  player: MusicKit.MusicKitInstance,
  params: PlaylistIdTracksRequestArgs,
): Promise<PlaylistIdTracksData> => { // PlaylistMediaPageResponse
  const p = safeParse(
    PlaylistIdTracksRequestArgsSchema,
    params,
  );
  if (!p.success) {
    throw new ValiError(p.issues);
  }

  const { id, limit, offset, isLibrary } = p.output;
  const library = isLibrary ?? true;
  const endpoint = library
    ? `/v1/me/library/playlists/${id}/tracks`
    : `/v1/catalog/${player.storefrontId}/playlists/${id}/tracks`;
  const res = await player.api.music(endpoint, {
    ...(limit ? { limit } : {}),
    ...(offset ? { offset } : {}),
  }) as unknown as PlaylistIdTracksAPIInput;
  const parsed = safeParse(
    PlaylistIdTracksAPISchema,
    res,
  );
  if (!parsed.success) {
    console.error(
      `[${endpoint}] Validation failed:`,
      JSON.stringify(parsed.issues, null, 2),
    );
    throw new ValiError(parsed.issues);
  }
  const { data } = parsed.output;
  return data;
};
